import type {
  CompositionV3DataQuery,
  PostingsV3DataQuery,
} from '@revelio/data-access';

import {
  DataType,
  type SingleDataSet,
  type TableData,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';
import { getEntityMonthlyValues, getEntitySeries } from '../utils/table-data';
import { trimEmptyTimeseriesEdges } from '../utils/trim-timeseries';
import { mapWeekRefsToMonthRefs } from './convert-demand-supply';
import {
  getWagePressureValue,
  latestWagePressure,
} from './convert-wage-pressure';
import {
  getTableMonths,
  getYoYGrowthAt,
  inferYoYPeriodOffsetFromDateRefs,
  inferYoYPeriodOffsetFromTable,
} from './shared-market-condition-utils';
import { calculateMarketTightnessMeterValue } from './z-score/calculate-meter-value';

type CompositionV3 = NonNullable<CompositionV3DataQuery['composition_v3']>;
type CompositionEntity = NonNullable<CompositionV3['entities']>[number];
type PostingResponse = NonNullable<PostingsV3DataQuery['postings_v3']>;
type PostingEntity = NonNullable<PostingResponse['entities'][number]>;
type PostingMetricPoint = NonNullable<
  PostingEntity['metrics']
>['active'][number];

const buildSalaryValuesAlignedToMonths = (
  entity: CompositionEntity,
  monthsLength: number
): number[] => {
  const values = new Array(monthsLength).fill(0);
  const salaryData = entity.metrics?.salary ?? [];
  for (const point of salaryData) {
    const idx = point.id;
    if (idx >= 0 && idx < monthsLength) {
      values[idx] = point.val;
    }
  }
  return values;
};

const computeDemandSupplyValue = (
  postings: number,
  headcount: number
): number => (headcount > 0 ? postings / headcount : 0);

const getLatestPostingMetrics = (
  entity: PostingEntity
): { active: number; timeToFill: number } => {
  const metrics = entity.metrics;
  const active = metrics?.active[metrics.active.length - 1]?.val ?? 0;
  const timeToFill =
    metrics?.time_to_fill[metrics.time_to_fill.length - 1]?.val ?? 0;
  return { active, timeToFill };
};

export function computeMarketTightnessDataSet({
  compositionData,
  postingData,
  monthRefs,
  weekRefs,
  isTimeseries = false,
}: {
  compositionData: CompositionEntity[];
  postingData: PostingEntity[];
  monthRefs: ReadonlyMap<number, string>;
  weekRefs: ReadonlyMap<number, string>;
  isTimeseries?: boolean;
}): SingleDataSet | TimeseriesDataSet {
  if (isTimeseries) {
    return computeMarketTightnessTimeseries({
      compositionData,
      postingData,
      monthRefs,
      weekRefs,
    });
  }

  return computeMarketTightnessMostRecent({
    compositionData,
    postingData,
    monthRefs,
  });
}

const buildLatestValueByMonthId = (
  points: PostingMetricPoint[],
  weekToMonthId: ReadonlyMap<number, number>
): Map<number, number> => {
  const valueByMonthId = new Map<number, number>();
  for (const point of points) {
    const monthId = weekToMonthId.get(point.id);
    if (monthId === undefined) {
      continue;
    }
    valueByMonthId.set(monthId, point.val);
  }
  return valueByMonthId;
};

function computeMarketTightnessTimeseries({
  compositionData,
  postingData,
  monthRefs,
  weekRefs,
}: {
  compositionData: CompositionEntity[];
  postingData: PostingEntity[];
  monthRefs: ReadonlyMap<number, string>;
  weekRefs: ReadonlyMap<number, string>;
}): TimeseriesDataSet {
  const months = Array.from(monthRefs.values());
  return marketTightnessTimeseries({
    composition: compositionData.map((entity) => ({
      id: String(entity.id),
      dim: entity.short_name ?? entity.long_name ?? 'Selected',
      salaries: buildSalaryValuesAlignedToMonths(entity, months.length),
      headcountByMonth: new Map(
        (entity.metrics?.headcount ?? []).map((point) => [point.id, point.val])
      ),
    })),
    postingData,
    months,
    weekToMonth: mapWeekRefsToMonthRefs(weekRefs, monthRefs),
  });
}

type MonthlyComposition = {
  id: string;
  dim: string;
  salaries: number[];
  headcountByMonth: ReadonlyMap<number, number>;
};

const marketTightnessTimeseries = ({
  composition,
  postingData,
  months,
  weekToMonth,
}: {
  composition: readonly MonthlyComposition[];
  postingData: PostingEntity[];
  months: string[];
  weekToMonth: ReadonlyMap<number, number>;
}): TimeseriesDataSet => {
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const postingsByEntityId = new Map(
    postingData.map((entity) => [String(entity.id), entity])
  );

  const data: TimeseriesData[] = [];

  for (const { id, dim, salaries, headcountByMonth } of composition) {
    const postingEntity = postingsByEntityId.get(id);
    if (!postingEntity) {
      continue;
    }

    const activeByMonthId = buildLatestValueByMonthId(
      postingEntity.metrics?.active ?? [],
      weekToMonth
    );
    const timeToFillByMonthId = buildLatestValueByMonthId(
      postingEntity.metrics?.time_to_fill ?? [],
      weekToMonth
    );

    const timeseries: TimeseriesDataPoint[] = [];

    for (let monthId = periodOffset; monthId < months.length; monthId++) {
      const date = months[monthId];
      if (!date) {
        continue;
      }

      const wageGrowth = getYoYGrowthAt({
        values: salaries,
        index: monthId,
        periodOffset,
      });
      const headcount = headcountByMonth.get(monthId);
      const active = activeByMonthId.get(monthId);
      const timeToFill = timeToFillByMonthId.get(monthId);

      const value =
        wageGrowth === null ||
        headcount === undefined ||
        active === undefined ||
        timeToFill === undefined
          ? null
          : calculateMarketTightnessMeterValue({
              wageGrowth,
              demandSupply: computeDemandSupplyValue(active, headcount),
              timeToFill,
            });

      timeseries.push({ date, value });
    }

    if (timeseries.length === 0 || timeseries.every((p) => p.value === null)) {
      continue;
    }

    data.push({ dim, timeseries, id });
  }

  return trimEmptyTimeseriesEdges(data);
};

function computeMarketTightnessMostRecent({
  compositionData,
  postingData,
  monthRefs,
}: {
  compositionData: CompositionEntity[];
  postingData: PostingEntity[];
  monthRefs: ReadonlyMap<number, string>;
}): SingleDataSet {
  return computeLatestMarketTightness({
    composition: compositionData.map((entity) => {
      const headcountData = entity.metrics?.headcount ?? [];
      return {
        id: String(entity.id),
        dim: entity.short_name ?? entity.long_name ?? 'Selected',
        headcount: headcountData[headcountData.length - 1]?.val ?? 0,
        wageGrowth: getWagePressureValue({
          entity,
          monthRefs,
          variant: 'value',
        }),
      };
    }),
    postingData,
  });
}

export const computeMarketTightnessFromTable = ({
  table,
  postingData,
  weekRefs,
  isTimeseries,
}: {
  table: TableData;
  postingData: PostingEntity[];
  weekRefs: ReadonlyMap<number, string>;
  isTimeseries: boolean;
}): SingleDataSet | TimeseriesDataSet => {
  if (isTimeseries) {
    const months = getTableMonths(table);
    const monthIndex = new Map(months.map((month, index) => [month, index]));
    const salaries = new Map(
      getEntityMonthlyValues(table, 'salary').map(({ value, byMonth }) => [
        value.id,
        months.map((month) => byMonth.get(month) ?? 0),
      ])
    );
    return marketTightnessTimeseries({
      composition: getEntityMonthlyValues(table, 'headcount').map(
        ({ value, byMonth }) => ({
          id: value.id,
          dim: value.label,
          salaries: salaries.get(value.id) ?? [],
          headcountByMonth: new Map(
            [...byMonth].flatMap(([month, headcount]) => {
              const index = monthIndex.get(month);
              return index === undefined ? [] : [[index, headcount] as const];
            })
          ),
        })
      ),
      postingData,
      months,
      weekToMonth: mapWeekRefsToMonthRefs(
        weekRefs,
        new Map(months.map((month, index) => [index, month]))
      ),
    });
  }

  const periodOffset = inferYoYPeriodOffsetFromTable(table);
  const salaries = new Map(
    getEntitySeries(table, 'salary').map(({ value, values }) => [
      value.id,
      values,
    ])
  );
  return computeLatestMarketTightness({
    composition: getEntitySeries(table, 'headcount').map(
      ({ value, values }) => ({
        id: value.id,
        dim: value.label,
        headcount: values[values.length - 1] ?? 0,
        wageGrowth: latestWagePressure({
          salaries: salaries.get(value.id) ?? [],
          periodOffset,
          variant: 'value',
        }),
      })
    ),
    postingData,
  });
};

type LatestComposition = {
  id: string;
  dim: string;
  headcount: number;
  wageGrowth: number | null;
};

const computeLatestMarketTightness = ({
  composition,
  postingData,
}: {
  composition: readonly LatestComposition[];
  postingData: PostingEntity[];
}): SingleDataSet => {
  const postingsByEntityId = new Map(
    postingData.map((entity) => [
      String(entity.id),
      getLatestPostingMetrics(entity),
    ])
  );

  const data: SingleDataSet['data'] = [];
  for (const { id, dim, headcount, wageGrowth } of composition) {
    const postings = postingsByEntityId.get(id);
    if (!postings || wageGrowth === null) continue;

    data.push({
      dim,
      value: calculateMarketTightnessMeterValue({
        wageGrowth,
        demandSupply: computeDemandSupplyValue(postings.active, headcount),
        timeToFill: postings.timeToFill,
      }),
      id,
    });
  }

  return { type: DataType.SINGLE, data };
};
